// src/pages/SubscriptionHire.jsx
// Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, { useState, useEffect, useMemo } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiStar,
  FiZap,
  FiShield,
  FiUsers,
  FiAward,
  FiTrendingUp,
  FiHeart,
  FiCalendar,
  FiRefreshCw,
  FiGrid,
  FiPackage,
  FiBookOpen,
  FiExternalLink,
} from 'react-icons/fi';
import { TbBrandFiverr } from 'react-icons/tb';
import {
  db,
  collection,
  getDocs,
  query,
  orderBy,
  where,
} from '../services/firebase';
import InquiryPopup from '../components/InquiryPopup';
import { useTheme } from '../contexts/ThemeContext';
import ReviewsSection from '../components/ReviewsSection';
import StatsSection from '../components/StatsSection';
import { FiverrCard } from '../components/FiverrCard';
import { motion, AnimatePresence } from 'framer-motion';
// ✅ REMOVED: ogImageService - No client-side OG generation
// import { ogImageService } from '../services/ogImageService';

// =============================================
// GLASSY DIVIDER
// =============================================
const GlassyDivider = () => {
  return (
    <div className="relative py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="relative h-px w-full overflow-hidden">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute inset-0 w-1/3"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================
// GLASSY CARD
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.4)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        boxShadow:
          '0 8px 32px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// LIQUID GRADIENT HERO
// =============================================
const SubscriptionHero = () => {
  return (
    <div className="relative mb-8 overflow-hidden rounded-3xl">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:via-transparent dark:to-purple-500/10" />

      <motion.div
        animate={{
          x: ['-20%', '15%', '-5%', '20%', '-20%'],
          y: ['-15%', '10%', '-10%', '15%', '-15%'],
          scale: [1, 1.3, 0.9, 1.2, 1],
          borderRadius: [
            '60% 40% 30% 70%',
            '40% 60% 70% 30%',
            '30% 70% 60% 40%',
            '70% 30% 40% 60%',
            '60% 40% 30% 70%',
          ],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-5 left-5 w-72 h-72 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #3b82f6 0%, #60a5fa 40%, transparent 70%)',
          opacity: 0.12,
        }}
      />

      <motion.div
        animate={{
          x: ['15%', '-15%', '10%', '-10%', '15%'],
          y: ['15%', '-10%', '20%', '-15%', '15%'],
          scale: [1.2, 0.9, 1.4, 0.8, 1.2],
          borderRadius: [
            '40% 60% 70% 30%',
            '60% 40% 30% 70%',
            '70% 30% 40% 60%',
            '30% 70% 60% 40%',
            '40% 60% 70% 30%',
          ],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-5 right-5 w-80 h-80 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #8b5cf6 0%, #a78bfa 40%, transparent 70%)',
          opacity: 0.1,
        }}
      />

      <motion.div
        animate={{
          x: ['-5%', '10%', '-10%', '5%', '-5%'],
          y: ['10%', '-15%', '5%', '-10%', '10%'],
          scale: [1, 1.2, 0.8, 1.3, 1],
          borderRadius: [
            '50% 30% 60% 40%',
            '30% 60% 40% 70%',
            '60% 40% 70% 30%',
            '40% 70% 30% 60%',
            '50% 30% 60% 40%',
          ],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #06b6d4 0%, #22d3ee 40%, transparent 70%)',
          opacity: 0.08,
        }}
      />

      <motion.div
        animate={{
          x: ['10%', '-20%', '15%', '-5%', '10%'],
          y: ['-5%', '15%', '-15%', '10%', '-5%'],
          scale: [0.8, 1.4, 0.9, 1.1, 0.8],
          borderRadius: [
            '70% 30% 50% 50%',
            '30% 70% 50% 50%',
            '50% 50% 70% 30%',
            '50% 50% 30% 70%',
            '70% 30% 50% 50%',
          ],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-20 left-20 w-60 h-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #ec4899 0%, #f472b6 40%, transparent 70%)',
          opacity: 0.06,
        }}
      />

      <div className="relative px-6 py-10 md:py-14 lg:py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.6)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiBookOpen className="w-4 h-4" style={{ color: '#3b82f6' }} />
          <span style={{ color: 'var(--muted)' }}>
            Flexible Subscription Plans
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight leading-[1.1]"
          style={{ color: 'var(--text)' }}
        >
          Choose the perfect
          <span className="block" style={{ color: 'var(--muted)' }}>
            plan for your needs
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
          style={{ color: 'var(--muted)', opacity: 0.8 }}
        >
          Flexible subscription options designed to scale with your business.
          Cancel anytime.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-6 flex items-center justify-center gap-3"
        >
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <span
            className="text-xs tracking-widest uppercase"
            style={{ color: 'var(--muted)', opacity: 0.3 }}
          >
            Subscribe
          </span>
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </motion.div>
      </div>
    </div>
  );
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

function SubscriptionHire() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [animatedSections, setAnimatedSections] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredPlan, setHoveredPlan] = useState(null);

  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  // ✅ REMOVED: metaData - No client-side OG generation
  // Social media scrapers get OG tags from server API
  // The OG tags are now served via /api/social-preview?page=subscription

  // Icon mapping
  const getIconComponent = iconName => {
    const icons = {
      FiCalendar,
      FiZap,
      FiStar,
      FiHeart,
      FiTrendingUp,
      FiUsers,
      FiAward,
      FiShield,
      FiPackage,
      FiGrid,
    };
    return icons[iconName] || FiCalendar;
  };

  // Animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -100px 0px' },
    );

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Fetch subscription plans from Firebase
  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, 'subscriptionPlans'),
        where('status', '==', 'active'),
        orderBy('order', 'asc'),
      );

      const snapshot = await getDocs(q);
      const plansList = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        plansList.push({
          id: doc.id,
          name: data.name,
          price: {
            monthly: parseInt(data.monthlyPrice) || 0,
            yearly: parseInt(data.yearlyPrice) || 0,
          },
          duration: {
            monthly: data.monthlyDuration || 'Month to month',
            yearly: data.yearlyDuration || 'Annual commitment',
          },
          description: data.description,
          features: data.features || [],
          icon: getIconComponent(data.icon),
          popular: data.popular || false,
          ctaText: data.ctaText || 'Get Started',
          savingsBadge: data.savingsBadge || 'Save 15%',
          order: data.order || 0,
          imageUrl: data.imageUrl || null,
          gradient: data.gradient || 'from-purple-500 to-pink-500',
          category: data.category || 'subscription',
        });
      });

      setSubscriptionPlans(plansList);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      setError('Failed to load subscription plans. Please try again later.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPlans();
  };

  const handleInquireClick = (e, plan) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPlan({
      id: plan.id,
      name: plan.name,
      price: plan.price,
      billingCycle: billingCycle,
      description: plan.description,
      features: plan.features,
    });
    setIsPopupOpen(true);
  };

  const calculateYearlySavings = (monthlyPrice, yearlyPrice) => {
    const yearlyTotal = monthlyPrice * 12;
    const savings = yearlyTotal - yearlyPrice;
    const savingsPercent = Math.round((savings / yearlyTotal) * 100);
    return savingsPercent > 0 ? savingsPercent : 0;
  };

  const benefits = [
    {
      icon: FiTrendingUp,
      title: 'Scale Your Business',
      description: 'Tools and features that grow with you',
    },
    {
      icon: FiShield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security and 99.9% uptime',
    },
    {
      icon: FiUsers,
      title: 'Team Collaboration',
      description: 'Work together seamlessly with your team',
    },
    {
      icon: FiHeart,
      title: 'Priority Support',
      description: 'Get help when you need it, day or night',
    },
  ];

  const faqs = [
    {
      q: 'Can I change my plan later?',
      a: 'Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.',
    },
    {
      q: 'Is there a long-term contract?',
      a: 'No, all monthly plans are month-to-month. Yearly plans require annual commitment but offer significant savings.',
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept all major credit cards, PayPal, and bank transfers for enterprise plans.',
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Absolutely. You can cancel your subscription at any time with no hidden fees.',
    },
  ];

  // ==================== IMPROVED SKELETON WITH SHIMMER ====================
  const SkeletonCard = () => (
    <div
      className="relative rounded-2xl border overflow-hidden"
      style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg)' }}
    >
      <div className="p-6 space-y-4">
        <div
          className="w-12 h-12 rounded-xl animate-pulse"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="h-7 w-32 rounded animate-pulse"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="h-4 w-full rounded animate-pulse"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div
          className="h-10 w-32 rounded animate-pulse"
          style={{ backgroundColor: 'var(--border)' }}
        />
        <div className="space-y-2">
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="h-4 w-full rounded animate-pulse"
              style={{ backgroundColor: 'var(--border)' }}
            />
          ))}
        </div>
        <div
          className="h-10 w-full rounded-lg animate-pulse"
          style={{ backgroundColor: 'var(--border)' }}
        />
      </div>
    </div>
  );

  // =============================================
  // PLAN CARD WITH SLIDE OVERLAY
  // =============================================
  const PlanCard = ({ plan, index }) => {
    const savingsPercent = calculateYearlySavings(
      plan.price.monthly,
      plan.price.yearly,
    );
    const IconComponent = plan.icon;
    const isHovered = hoveredPlan === plan.id;

    // Schema markup for individual plan
    const planSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: plan.name,
      description: plan.description,
      provider: { '@type': 'Person', name: 'WD Hajbul' },
      offers: {
        '@type': 'Offer',
        price:
          billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly,
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price:
            billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly,
          priceCurrency: 'USD',
          unitPricingMeasurement: {
            '@type': 'UnitPricingMeasurement',
            unitCode: billingCycle === 'monthly' ? 'MON' : 'ANN',
          },
        },
      },
    };

    return (
      <motion.div
        variants={itemVariants}
        className="group relative"
        onMouseEnter={() => setHoveredPlan(plan.id)}
        onMouseLeave={() => setHoveredPlan(null)}
      >
        <Link
          to={`/subscription/${plan.id}`}
          className={`relative rounded-2xl transition-all duration-300 hover:translate-y-[-8px] block h-full ${
            plan.popular ? 'ring-2' : ''
          }`}
          style={{
            border: `1px solid ${isHovered ? 'var(--text)' : 'var(--border)'}`,
            backgroundColor: 'var(--bg)',
            ringColor: 'var(--text)',
          }}
        >
          {/* Schema markup for the plan */}
          <script type="application/ld+json">
            {JSON.stringify(planSchema)}
          </script>

          {plan.popular && (
            <div
              className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap z-10"
              style={{
                backgroundColor: 'var(--text)',
                color: 'var(--bg)',
              }}
            >
              Most Popular
            </div>
          )}
          <div className="p-6">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <IconComponent
                className="w-6 h-6"
                style={{ color: 'var(--text)' }}
              />
            </div>
            <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
            <p
              className="text-sm mb-4 line-clamp-2"
              style={{ color: 'var(--muted)' }}
            >
              {plan.description}
            </p>
            <div className="mb-4">
              <span className="text-4xl font-bold">
                $
                {billingCycle === 'monthly'
                  ? plan.price.monthly
                  : plan.price.yearly}
              </span>
              <span className="text-sm" style={{ color: 'var(--muted)' }}>
                /{billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
              {billingCycle === 'yearly' && savingsPercent > 0 && (
                <div className="text-xs text-green-500 mt-1">
                  Save {savingsPercent}% annually
                </div>
              )}
            </div>
            <div
              className="flex items-center gap-1 text-xs mb-4"
              style={{ color: 'var(--muted)' }}
            >
              <FiCalendar className="w-3 h-3" />
              <span>
                {billingCycle === 'monthly'
                  ? plan.duration.monthly
                  : plan.duration.yearly}
              </span>
            </div>
            <ul className="space-y-2 mb-6">
              {plan.features.slice(0, 6).map((feature, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-sm"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiCheckCircle
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ color: 'var(--text)' }}
                  />
                  <span className="line-clamp-1">{feature}</span>
                </li>
              ))}
              {plan.features.length > 6 && (
                <li className="text-xs ml-6" style={{ color: 'var(--muted)' }}>
                  +{plan.features.length - 6} more features
                </li>
              )}
            </ul>

            {/* Action Buttons with Slide Overlay Effect */}
            <div className="relative overflow-hidden rounded-lg">
              <motion.div
                className="absolute inset-0 rounded-lg"
                style={{
                  background:
                    'linear-gradient(135deg, var(--text), var(--text))',
                  opacity: 0.08,
                }}
                initial={{ x: '-100%' }}
                animate={{ x: isHovered ? '0%' : '-100%' }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              />

              <button
                onClick={e => handleInquireClick(e, plan)}
                className="relative w-full flex items-center justify-center gap-2 py-2.5 rounded-lg transition-all duration-300 hover:gap-3 cursor-pointer text-sm z-10"
                style={{
                  backgroundColor: plan.popular ? 'var(--text)' : 'transparent',
                  color: plan.popular ? 'var(--bg)' : 'var(--text)',
                  border: !plan.popular ? '1px solid var(--border)' : 'none',
                }}
              >
                <span>{plan.ctaText || 'Inquire Now'}</span>
                <FiArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  };

  // Inject shimmer animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      .animate-shimmer {
        animation: shimmer 1.8s infinite;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/social-preview?page=subscription
      */}
      <Helmet>
        <title>Subscription Plans | WD Hajbul - Design Services</title>
        <meta
          name="description"
          content="Explore flexible subscription plans for professional design services. Logo design, branding, and product visuals."
        />
        <meta
          name="keywords"
          content="subscription, design subscription, monthly design, yearly design, branding subscription, product design subscription"
        />
        <link rel="canonical" href={window.location.href} />
        <meta name="robots" content="index, follow" />

        {/* Site Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'WD Hajbul Subscription Plans',
            url: window.location.origin,
            description:
              'Flexible subscription plans for professional design services.',
          })}
        </script>
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* ==================== HERO SECTION - Liquid Gradient ==================== */}
        <section className="pt-32 pb-20 max-w-7xl mx-auto max-sm:p-5 max-sm:pt-24">
          <SubscriptionHero />
        </section>

        <StatsSection
          fetchProjectCount={true}
          columns={4}
          variant="default"
          showBorder={true}
        />

        <GlassyDivider />

        {/* ==================== PRICING CARDS ==================== */}
        <section className="animate-on-scroll py-12 px-4 max-w-6xl mx-auto transition-all duration-700 delay-100">
          {/* Billing Toggle - Glassy */}
          <div className="flex justify-center mb-12">
            <div
              className="inline-flex p-1 rounded-full backdrop-blur-sm"
              style={{
                backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
                border: '1px solid rgba(var(--text-rgb), 0.06)',
              }}
            >
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full text-sm transition-all duration-300 ${
                  billingCycle === 'monthly'
                    ? 'bg-black dark:bg-white text-white dark:text-black'
                    : ''
                }`}
                style={{
                  backgroundColor:
                    billingCycle === 'monthly'
                      ? isDark
                        ? '#ffffff'
                        : '#000000'
                      : 'transparent',
                  color:
                    billingCycle === 'monthly'
                      ? isDark
                        ? '#000000'
                        : '#ffffff'
                      : 'var(--text)',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-full text-sm transition-all duration-300 ${
                  billingCycle === 'yearly'
                    ? 'bg-black dark:bg-white text-white dark:text-black'
                    : ''
                }`}
                style={{
                  backgroundColor:
                    billingCycle === 'yearly'
                      ? isDark
                        ? '#ffffff'
                        : '#000000'
                      : 'transparent',
                  color:
                    billingCycle === 'yearly'
                      ? isDark
                        ? '#000000'
                        : '#ffffff'
                      : 'var(--text)',
                }}
              >
                Yearly <span className="text-xs opacity-70">Save 15%</span>
              </button>
            </div>
          </div>

          {/* Refresh Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80 backdrop-blur-sm"
              style={{
                border: '1px solid rgba(var(--text-rgb), 0.06)',
                color: 'var(--text)',
                backgroundColor: 'rgba(var(--bg-rgb), 0.2)',
              }}
            >
              <FiRefreshCw
                className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`}
              />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiCalendar
                  className="w-10 h-10"
                  style={{ color: 'var(--muted)' }}
                />
              </div>
              <h3
                className="text-xl font-semibold mb-2"
                style={{ color: 'var(--text)' }}
              >
                {error}
              </h3>
              <button
                onClick={handleRefresh}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiRefreshCw className="w-4 h-4" /> Try Again
              </button>
            </div>
          ) : subscriptionPlans.length === 0 ? (
            <div className="text-center py-12">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiCalendar
                  className="w-10 h-10"
                  style={{ color: 'var(--muted)' }}
                />
              </div>
              <h3
                className="text-xl font-semibold mb-2"
                style={{ color: 'var(--text)' }}
              >
                No plans available
              </h3>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                Check back soon for subscription plans!
              </p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid md:grid-cols-3 gap-6"
            >
              {subscriptionPlans.map((plan, index) => (
                <PlanCard key={plan.id} plan={plan} index={index} />
              ))}
            </motion.div>
          )}
        </section>

        <GlassyDivider />

        {/* ==================== BENEFITS SECTION ==================== */}
        <section
          className="animate-on-scroll py-20 px-4 transition-all duration-700 delay-200"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm uppercase tracking-wider opacity-60 mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Everything you need to <br />
                <span className="italic">succeed</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="relative group"
                >
                  <div
                    className="text-center p-6 rounded-2xl transition-all duration-300 hover:scale-105 relative overflow-hidden"
                    style={{ backgroundColor: 'var(--bg)' }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background:
                          'linear-gradient(135deg, var(--text), transparent 70%)',
                        opacity: 0.03,
                      }}
                    />
                    <div className="relative z-10">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
                        style={{
                          backgroundColor: 'var(--border)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        }}
                      >
                        <benefit.icon
                          className="w-7 h-7 transition-transform duration-300 group-hover:rotate-6"
                          style={{ color: 'var(--text)' }}
                        />
                      </div>
                      <h3 className="font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-sm" style={{ color: 'var(--muted)' }}>
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== FAQ SECTION ==================== */}
        <section className="animate-on-scroll py-20 px-4 max-w-4xl mx-auto transition-all duration-700 delay-300">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-wider opacity-60 mb-4 block">
              FAQs
            </span>
            <h2 className="text-3xl md:text-4xl font-bold">
              Frequently asked
              <br />
              <span className="italic">questions</span>
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <GlassyCard
                key={idx}
                className="p-5 rounded-xl transition-all duration-300 hover:translate-x-1 cursor-pointer"
              >
                <h3
                  className="font-semibold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  {faq.q}
                </h3>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  {faq.a}
                </p>
              </GlassyCard>
            ))}
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== FIVERR CARD ==================== */}
        <section className="py-12 px-4 max-w-7xl mx-auto">
          <FiverrCard variant="standard" />
        </section>

        <GlassyDivider />

        {/* ==================== TESTIMONIALS ==================== */}
        <ReviewsSection />

        {/* ==================== CTA SECTION ==================== */}
        <section className="animate-on-scroll py-20 px-4 transition-all duration-700 delay-400">
          <GlassyCard className="max-w-4xl mx-auto text-center p-12 rounded-3xl relative overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                opacity: 0.05,
              }}
            />

            <div className="relative z-10">
              <FiHeart
                className="w-16 h-16 mx-auto mb-4 animate-pulse"
                style={{ color: 'var(--text)' }}
              />
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: 'var(--text)' }}
              >
                Ready to get started?
              </h2>
              <p
                className="mb-8 max-w-md mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                Join hundreds of satisfied customers who trust us with their
                business.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                {subscriptionPlans.length > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.03, gap: '0.75rem' }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() =>
                      handleInquireClick(
                        null,
                        subscriptionPlans.find(p => p.popular) ||
                          subscriptionPlans[0],
                      )
                    }
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    Start Free Trial <FiArrowRight className="w-4 h-4" />
                  </motion.button>
                )}
                <NavLink
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full transition-all duration-300"
                  style={{ border: '1px solid var(--border)' }}
                >
                  Contact Sales
                </NavLink>
              </div>
            </div>
          </GlassyCard>
        </section>

        {/* Popup */}
        <InquiryPopup
          isOpen={isPopupOpen}
          onClose={() => {
            setIsPopupOpen(false);
            setSelectedPlan(null);
          }}
          packageData={selectedPlan}
          type="subscription"
        />

        <style jsx>{`
          @keyframes shimmer {
            0% {
              transform: translateX(-100%) rotate(12deg);
            }
            100% {
              transform: translateX(200%) rotate(12deg);
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default SubscriptionHire;
